import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Radio, ArrowRight, MessageSquare, Zap, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './Navbar.module.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { language, selectLanguage, t } = useLanguage();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('mousedown', handleOutsideClick);
    return () => window.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const navLinks = [
    { name: t('nav_tech'), href: '#about' },
    { name: t('nav_samples'), href: '#samples' },
    { name: t('nav_pricing'), href: '#pricing' },
    { name: t('nav_services'), href: '#services' },
  ];

  const languages = [
    { code: 'en', label: 'EN', name: 'English' },
    { code: 'hi', label: 'HI', name: 'हिंदी' },
    { code: 'mr', label: 'MR', name: 'मराठी' },
    { code: 'kn', label: 'KN', name: 'ಕನ್ನಡ' },
    { code: 'bn', label: 'BN', name: 'বাংলা' },
    { code: 'te', label: 'TE', name: 'తెలుగు' },
    { code: 'ta', label: 'TA', name: 'தமிழ்' },
    { code: 'gu', label: 'GU', name: 'ગુજરાતી' },
  ];

  const currentLangLabel = languages.find(l => l.code === language)?.label || 'EN';

  return (
    <nav className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
      <div className={styles.navContainer}>
        <a href="#home" className={styles.logo}>
          <img src="/logo.png" alt="Ananta Anchor Logo" className={styles.logoImg} />
        </a>

        <div className={styles.desktopWrapper}>
          <div className={styles.navLinks}>
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className={styles.navLink}>
                {link.name}
              </a>
            ))}
          </div>

          {/* Desktop Language Switcher */}
          <div className={styles.langSelectorContainer} ref={dropdownRef}>
            <button 
              className={styles.langSelectorBtn} 
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Globe size={16} />
              <span>{currentLangLabel}</span>
              <ChevronDown size={12} className={`${styles.chevron} ${dropdownOpen ? styles.chevronOpen : ''}`} />
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div 
                  className={styles.langDropdown}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  {languages.map((lang) => (
                    <button 
                      key={lang.code}
                      className={`${styles.dropdownItem} ${language === lang.code ? styles.activeLang : ''}`}
                      onClick={() => {
                        selectLanguage(lang.code);
                        setDropdownOpen(false);
                      }}
                    >
                      <span className={styles.langItemNative}>{lang.name}</span>
                      <span className={styles.langItemLabel}>{lang.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a href="#pricing" className={styles.ctaBtn}>
            {t('nav_demo_btn')} <Zap size={14} fill="currentColor" />
          </a>
        </div>

        <button className={styles.mobileToggle} onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className={styles.mobileMenuOverlay}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
          >
            <div className={styles.menuContent}>
              <div className={styles.menuTickerBar}>
                <div className={styles.liveLabel}><Radio size={14} className={styles.pulse} /> LIVE MENU</div>
                <div className={styles.menuTickerText}>START YOUR NEWS CHANNEL • AI BROADCAST READY • </div>
              </div>

              <div className={styles.mobileLinksStack}>
                {navLinks.map((link, idx) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    className={styles.mobileNavLink}
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 * idx }}
                    onClick={() => setIsOpen(false)}
                  >
                    <span className={styles.linkNum}>0{idx + 1}</span>
                    <span className={styles.linkTitle}>{link.name}</span>
                    <ArrowRight className={styles.arrow} size={20} />
                  </motion.a>
                ))}
              </div>

              {/* Mobile Language Switcher Section */}
              <div className={styles.mobileLangSection}>
                <div className={styles.mobileLangTitle}>
                  <Globe size={16} /> Choose Language / भाषा चुनें
                </div>
                <div className={styles.mobileLangGrid}>
                  {languages.map((lang) => (
                    <button 
                      key={lang.code}
                      className={`${styles.mobileLangBtn} ${language === lang.code ? styles.activeMobileLang : ''}`}
                      onClick={() => {
                        selectLanguage(lang.code);
                        setIsOpen(false);
                      }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>

              <a href="https://wa.me/917533928185" className={styles.whatsappBtn}>
                <MessageSquare size={20} /> {t('nav_chat_wa')}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;